import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { formatDate, formatDateTime, formatCurrency } from '../utils/dateUtils';
import toast from 'react-hot-toast';

const InvoiceDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [editingDueDate, setEditingDueDate] = useState(false);
  const [newDueDate, setNewDueDate] = useState('');

  useEffect(() => {
    loadInvoice();
  }, [id]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const response = await apiService.getInvoice(id);
      setInvoice(response.data);
    } catch (error) {
      console.error('Erro ao carregar fatura:', error);
      alert('Erro ao carregar fatura');
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action, confirmMsg = null) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;

    try {
      setActionLoading(true);

      switch (action) {
        case 'integrate':
          await apiService.integrateInvoiceAsaas(id);
          alert('✅ Integração com Asaas concluída!');
          break;
        case 'generatePdf':
          await apiService.generateInvoicePDF(id);
          alert('✅ PDF gerado com sucesso!');
          break;
        case 'finalize':
          await apiService.finalizeInvoice(id);
          alert('✅ Fatura finalizada (Asaas + PDF)!');
          break;
        case 'sendEmail':
          await apiService.sendInvoiceEmail(id);
          alert('✅ Email enviado!');
          break;
        case 'sendWhatsApp':
          await apiService.sendInvoiceWhatsApp(id);
          alert('✅ WhatsApp enviado!');
          break;
        case 'markPaid':
          await apiService.markInvoiceAsPaid(id);
          alert('✅ Fatura marcada como paga!');
          break;
        case 'cancel':
          const reason = prompt('Motivo do cancelamento:');
          if (!reason) return;
          await apiService.cancelInvoice(id, reason);
          alert('✅ Fatura cancelada!');
          break;
        case 'receipt':
          await apiService.generatePaymentReceipt(id);
          alert('✅ Comprovante gerado!');
          break;
      }

      loadInvoice();
    } catch (error) {
      alert('Erro: ' + (error.response?.data?.message || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      'PENDING': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: '⏳', label: 'Pendente' },
      'PAID': { color: 'bg-green-100 text-green-800 border-green-200', icon: '✅', label: 'Paga' },
      'OVERDUE': { color: 'bg-red-100 text-red-800 border-red-200', icon: '🔴', label: 'Vencida' },
      'CANCELLED': { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: '⚫', label: 'Cancelada' }
    };
    return configs[status] || configs['PENDING'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Fatura não encontrada</p>
      </div>
    );
  }

  const statusConfig = getStatusConfig(invoice.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/invoices')}
            className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
          >
            ← Voltar
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Fatura #{invoice.invoice_number}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Emitida em {formatDate(invoice.issue_date)}
            </p>
          </div>
        </div>

        <div className={`px-6 py-3 rounded-xl border-2 ${statusConfig.color} font-bold text-lg`}>
          {statusConfig.icon} {statusConfig.label}
        </div>
      </div>

      {/* Actions Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap gap-3">
          {!invoice.asaas_charge_id && invoice.status !== 'CANCELLED' && (
            <button
              onClick={() => handleAction('integrate')}
              disabled={actionLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              🔗 Integrar Asaas
            </button>
          )}

          {invoice.asaas_charge_id && !invoice.pdf_path && (
            <button
              onClick={() => handleAction('generatePdf')}
              disabled={actionLoading}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              📄 Gerar PDF
            </button>
          )}

          {!invoice.asaas_charge_id && !invoice.pdf_path && invoice.status !== 'CANCELLED' && (
            <button
              onClick={() => handleAction('finalize')}
              disabled={actionLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              ⚡ Finalizar (Asaas + PDF)
            </button>
          )}

          {invoice.pdf_path && (
            <>
              <a
                href={invoice.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                📥 Baixar PDF
              </a>

              <button
                onClick={() => handleAction('sendEmail')}
                disabled={actionLoading}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
              >
                📧 Enviar Email
              </button>

              <button
                onClick={() => handleAction('sendWhatsApp')}
                disabled={actionLoading}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
              >
                📱 Enviar WhatsApp
              </button>
            </>
          )}

          {invoice.status === 'PENDING' && (
            <button
              onClick={() => handleAction('markPaid', 'Marcar esta fatura como paga?')}
              disabled={actionLoading}
              className="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 disabled:opacity-50"
            >
              ✅ Marcar como Paga
            </button>
          )}

          {invoice.status === 'PAID' && (
            <button
              onClick={() => handleAction('receipt')}
              disabled={actionLoading}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              🧾 Gerar Comprovante
            </button>
          )}

          {invoice.status !== 'CANCELLED' && invoice.status !== 'PAID' && (
            <button
              onClick={() => handleAction('cancel', 'Tem certeza que deseja cancelar esta fatura?')}
              disabled={actionLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              ❌ Cancelar Fatura
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna Principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Informações da Fatura */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              📋 Informações da Fatura
            </h2>

            <div className="grid grid-cols-2 gap-4">
              <InfoItem label="Número" value={`#${invoice.invoice_number}`} />
              <InfoItem label="Contrato" value={invoice.contract_id || 'Avulsa'} />
              <InfoItem label="Data de Emissão" value={formatDate(invoice.issue_date)} />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Data de Vencimento</p>
                {editingDueDate ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={newDueDate}
                      onChange={(e) => setNewDueDate(e.target.value)}
                      className="px-2 py-1 border border-blue-400 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={async () => {
                        try {
                          const hasCharge = !!invoice.asaas_charge_id;
                          const regenerate = hasCharge ? window.confirm('Esta fatura tem cobrança no Asaas.\n\nDeseja CANCELAR a cobrança atual e GERAR UMA NOVA com o novo vencimento?') : false;
                          await apiService.updateInvoiceDueDate(id, newDueDate, regenerate);
                          toast.success('Vencimento atualizado!');
                          setEditingDueDate(false);
                          loadInvoice();
                        } catch (error) {
                          toast.error('Erro: ' + (error.response?.data?.message || error.message));
                        }
                      }}
                      className="px-2 py-1 bg-green-600 text-white rounded-lg text-xs hover:bg-green-700"
                    >
                      Salvar
                    </button>
                    <button
                      onClick={() => setEditingDueDate(false)}
                      className="px-2 py-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs hover:bg-gray-400"
                    >
                      Cancelar
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {formatDate(invoice.due_date)}
                    </p>
                    {invoice.status !== 'PAID' && invoice.status !== 'CANCELLED' && (
                      <button
                        onClick={() => {
                          setNewDueDate(invoice.due_date ? new Date(invoice.due_date).toISOString().split('T')[0] : '');
                          setEditingDueDate(true);
                        }}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Editar vencimento"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>
              <InfoItem label="Período Início" value={formatDate(invoice.period_start)} />
              <InfoItem label="Período Fim" value={formatDate(invoice.period_end)} />
              {invoice.payment_date && (
                <InfoItem label="Data do Pagamento" value={formatDate(invoice.payment_date)} highlight />
              )}
            </div>
          </div>

          {/* Cliente */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              👤 Cliente
            </h2>

            <div className="space-y-2">
              <p className="text-xl font-bold text-gray-900 dark:text-white">{invoice.client_name}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <strong>CPF/CNPJ:</strong> {invoice.client_document}
              </p>
              {invoice.client_email && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <strong>Email:</strong> {invoice.client_email}
                </p>
              )}
              {invoice.client_phone && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <strong>Telefone:</strong> {invoice.client_phone}
                </p>
              )}
              {invoice.client_address && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <strong>Endereço:</strong> {invoice.client_address}
                  {invoice.client_city && `, ${invoice.client_city}`}
                  {invoice.client_state && ` - ${invoice.client_state}`}
                </p>
              )}
            </div>
          </div>

          {/* Itens */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              📦 Itens da Fatura
            </h2>

            <div className="space-y-3">
              {invoice.items && invoice.items.map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between items-start p-4 bg-gray-50 dark:bg-gray-750 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {item.item_number}. {item.description}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {item.quantity} {item.unit} × {formatCurrency(item.unit_price)}
                    </p>
                    {item.vehicle_model && (
                      <p className="text-xs text-gray-500 mt-1">
                        🚛 {item.vehicle_model} - {item.vehicle_plate}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg text-gray-900 dark:text-white">
                      {formatCurrency(item.total_price)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline / Logs */}
          {invoice.logs && invoice.logs.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                📅 Timeline
              </h2>

              <div className="space-y-4">
                {invoice.logs.map((log, index) => (
                  <div key={log.id} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                      {index < invoice.logs.length - 1 && (
                        <div className="w-0.5 h-full bg-gray-300 dark:bg-gray-600 mt-1"></div>
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {log.description}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {formatDateTime(log.created_at)}
                        {log.user_name && ` • ${log.user_name}`}
                      </p>
                      {log.extra_data && (
                        <pre className="text-xs text-gray-600 dark:text-gray-400 mt-2 bg-gray-100 dark:bg-gray-750 p-2 rounded">
                          {log.extra_data}
                        </pre>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Valores */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              💰 Valores
            </h2>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(invoice.subtotal)}
                </span>
              </div>

              {invoice.insurance_value > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Seguro:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(invoice.insurance_value)}
                  </span>
                </div>
              )}

              {invoice.discount_value > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Desconto:</span>
                  <span className="font-semibold">
                    - {formatCurrency(invoice.discount_value)}
                  </span>
                </div>
              )}

              <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-900 dark:text-white">Total:</span>
                  <span className="text-2xl font-bold text-red-600">
                    {formatCurrency(invoice.total_value)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Status de Envio */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              📊 Status de Envio
            </h2>

            <div className="space-y-4">
              {/* Fatura Enviada */}
              <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  invoice.email_sent_at || invoice.whatsapp_sent_at
                    ? 'bg-green-100 dark:bg-green-900/30'
                    : 'bg-gray-100 dark:bg-gray-700'
                }`}>
                  {invoice.email_sent_at || invoice.whatsapp_sent_at ? (
                    <span className="text-green-600 dark:text-green-400">✓</span>
                  ) : (
                    <span className="text-gray-400">○</span>
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 dark:text-white text-sm">
                    Fatura Enviada
                  </p>
                  {invoice.email_sent_at || invoice.whatsapp_sent_at ? (
                    <div className="space-y-1 mt-1">
                      {invoice.email_sent_at && (
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          📧 Email: {formatDateTime(invoice.email_sent_at)}
                        </p>
                      )}
                      {invoice.whatsapp_sent_at && (
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          📱 WhatsApp: {formatDateTime(invoice.whatsapp_sent_at)}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Aguardando envio
                    </p>
                  )}
                </div>
              </div>

              {/* Boleto Enviado */}
              <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  invoice.boleto_url && (invoice.email_sent_at || invoice.whatsapp_sent_at)
                    ? 'bg-green-100 dark:bg-green-900/30'
                    : 'bg-gray-100 dark:bg-gray-700'
                }`}>
                  {invoice.boleto_url && (invoice.email_sent_at || invoice.whatsapp_sent_at) ? (
                    <span className="text-green-600 dark:text-green-400">✓</span>
                  ) : (
                    <span className="text-gray-400">○</span>
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 dark:text-white text-sm">
                    Boleto Enviado
                  </p>
                  {invoice.boleto_url && (invoice.email_sent_at || invoice.whatsapp_sent_at) ? (
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                      Link do boleto compartilhado
                    </p>
                  ) : invoice.boleto_url ? (
                    <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                      Boleto gerado, aguardando envio
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Boleto não gerado
                    </p>
                  )}
                </div>
              </div>

              {/* Pagamento */}
              <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  invoice.status === 'PAID'
                    ? 'bg-green-100 dark:bg-green-900/30'
                    : invoice.status === 'OVERDUE'
                    ? 'bg-red-100 dark:bg-red-900/30'
                    : 'bg-gray-100 dark:bg-gray-700'
                }`}>
                  {invoice.status === 'PAID' ? (
                    <span className="text-green-600 dark:text-green-400">✓</span>
                  ) : invoice.status === 'OVERDUE' ? (
                    <span className="text-red-600 dark:text-red-400">!</span>
                  ) : (
                    <span className="text-gray-400">○</span>
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 dark:text-white text-sm">
                    Pagamento
                  </p>
                  {invoice.status === 'PAID' ? (
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                      ✅ Pago em {formatDate(invoice.payment_date)}
                    </p>
                  ) : invoice.status === 'OVERDUE' ? (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      ⚠️ Vencido em {formatDate(invoice.due_date)}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Aguardando pagamento
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Dados de Pagamento */}
          {invoice.asaas_charge_id && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                💳 Pagamento
              </h2>

              <div className="space-y-3 text-sm">
                {invoice.asaas_charge_id && (
                  <p className="text-gray-600 dark:text-gray-400">
                    <strong>ID Asaas:</strong> {invoice.asaas_charge_id}
                  </p>
                )}

                {invoice.boleto_url && (
                  <a
                    href={invoice.boleto_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-4 py-2 bg-blue-600 text-white text-center rounded-lg hover:bg-blue-700"
                  >
                    📄 Ver Boleto
                  </a>
                )}

                {invoice.pix_copy_paste && (
                  <div>
                    <p className="text-gray-700 dark:text-gray-300 font-semibold mb-2">
                      🔑 Código PIX:
                    </p>
                    <div className="bg-gray-100 dark:bg-gray-750 p-3 rounded-lg">
                      <code className="text-xs break-all text-gray-800 dark:text-gray-200">
                        {invoice.pix_copy_paste}
                      </code>
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(invoice.pix_copy_paste);
                        alert('✅ Código PIX copiado!');
                      }}
                      className="mt-2 w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                    >
                      📋 Copiar Código PIX
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Envios */}
          {(invoice.email_sent_at || invoice.whatsapp_sent_at) && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                📬 Envios
              </h2>

              <div className="space-y-2 text-sm">
                {invoice.email_sent_at && (
                  <p className="text-gray-600 dark:text-gray-400">
                    <strong>Email:</strong> {formatDateTime(invoice.email_sent_at)}
                  </p>
                )}

                {invoice.whatsapp_sent_at && (
                  <p className="text-gray-600 dark:text-gray-400">
                    <strong>WhatsApp:</strong> {formatDateTime(invoice.whatsapp_sent_at)}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente auxiliar
const InfoItem = ({ label, value, highlight = false }) => (
  <div>
    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</p>
    <p className={`font-semibold ${highlight ? 'text-green-600' : 'text-gray-900 dark:text-white'}`}>
      {value}
    </p>
  </div>
);

export default InvoiceDetailsPage;
